import { AdminApiError } from "./admin-users-api.js";
import { csrfHeaders } from "../auth/session-api.js";

export interface TeamUsage {
  teamId: string;
  campaignCount: number;
}

export async function getTeamUsage(
  teamIds: readonly string[],
): Promise<TeamUsage[]> {
  if (teamIds.length === 0) return [];
  const params = new URLSearchParams();
  teamIds.forEach((teamId) => params.append("teamId", teamId));
  const response = (await request(
    `/api/v1/lama/admin/team-usage?${params.toString()}`,
  )) as { items: TeamUsage[] };
  return response.items;
}

export type IdentityActivitySource =
  | "directory"
  | "provisioning"
  | "access"
  | "teams";
export type IdentityActivityStatus = "success" | "failure";

export interface IdentityActivityItem {
  id: string;
  occurredAt: string;
  source: IdentityActivitySource;
  status: IdentityActivityStatus;
  title: string;
  detail: string | null;
  actorName: string | null;
  action: { type: "retry_projection"; syncId: string } | null;
}

export interface IdentityActivityPage {
  items: IdentityActivityItem[];
  nextCursor: string | null;
}

export async function listIdentityActivity(
  options: {
    source?: IdentityActivitySource;
    status?: IdentityActivityStatus;
    failuresOnly?: boolean;
    search?: string;
    from?: string;
    to?: string;
    cursor?: string | null;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<IdentityActivityPage> {
  const params = new URLSearchParams();
  if (options.source) params.set("source", options.source);
  if (options.status) params.set("status", options.status);
  if (options.failuresOnly) params.set("failuresOnly", "true");
  if (options.search) params.set("q", options.search);
  if (options.from) params.set("from", options.from);
  if (options.to) params.set("to", options.to);
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  return (await request(
    `/api/auth/admin/identity-activity?${params.toString()}`,
    signal ? { signal } : {},
  )) as IdentityActivityPage;
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new AdminApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  return response.json();
}
